import PublicLayout from "@/components/layout/PublicLayout";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import Seo, { buildFaqSchema } from "@/components/Seo";

const groups = [
  {
    cat: "Over OfferteExpert",
    items: [
      ["Wat is OfferteExpert en hoe werkt het?", "OfferteExpert is een onafhankelijk platform dat offertes vergelijkt met actuele marktprijzen in jouw regio. Je upload je offerte, betaalt €4,99 en ontvangt binnen 60 seconden een gedetailleerd rapport."],
      ["Wie staat achter OfferteExpert?", "OfferteExpert is een product van Webtron BV, gevestigd in Waregem (België), BTW BE0793944703."],
      ["Voor welke landen is OfferteExpert beschikbaar?", "Op dit moment ondersteunen we België en Nederland. We werken aan uitbreiding naar andere Europese landen."],
      ["Is OfferteExpert onafhankelijk?", "Ja. We hebben geen banden met aannemers, leveranciers of dienstverleners. Onze enige bron van inkomsten is jouw analyse."],
      ["Mag een bedrijf of zelfstandige ook een offerte laten checken?", "Nee. OfferteExpert is uitsluitend bedoeld voor PARTICULIEREN (B2C). Onze marktreferenties zijn afgestemd op consumentenprijzen en niet op zakelijke tarieven, aankoopcondities of B2B-relaties. Voor zakelijke analyses raden we een gespecialiseerd adviesbureau aan."],
    ],
  },
  {
    cat: "De Analyse",
    items: [
      ["Hoe nauwkeurig is de analyse?", "Onze referentieprijzen zijn gebaseerd op duizenden Belgische en Nederlandse markttarieven. De analyse is een betrouwbare indicatie maar geen vervanging voor professioneel advies."],
      ["Welke soorten offertes kan ik uploaden?", "Renovatie, elektriciteit, schilderwerk, tuinaanleg, IT, verhuizing, onderhoud, en meer. Je kan ook 'Andere' kiezen voor een specifiek type."],
      ["Hoe lang duurt een analyse?", "Doorgaans 30 tot 60 seconden. Je krijgt een e-mail zodra het rapport klaar is."],
      ["Wat als mijn offerte onduidelijk is of weinig detail bevat?", "Dan vragen we je om manueel de bedragen in te typen. Hoe meer detail, hoe nauwkeuriger het resultaat."],
      ["Wordt rekening gehouden met BTW?", "Ja. We onderscheiden 6% (renovatie >10 jaar) en 21% en geven duidelijk aan welk tarief van toepassing is."],
      ["Hoe worden marktprijzen bepaald?", "We combineren openbare bronnen, leveranciers- en aannemersdata, en houden rekening met regionale verschillen."],
      ["Wat betekent de score van 0 tot 100?", "Een score boven 75 betekent eerlijk geprijsd. Tussen 50-75 zijn er aandachtspunten. Onder 50 zijn er meerdere posten die te hoog zijn."],
      ["Kan ik een offerte in het Frans of Engels uploaden?", "Op dit moment ondersteunen we voornamelijk Nederlandstalige offertes. Andere talen werken vaak ook, maar de nauwkeurigheid kan lager zijn."],
    ],
  },
  {
    cat: "Privacy & Veiligheid",
    items: [
      ["Hoe lang worden mijn gegevens bewaard?", "Documenten en analyseresultaten: 30 dagen, daarna automatisch en permanent verwijderd. Betalingsgegevens: 7 jaar (wettelijke verplichting)."],
      ["Worden mijn documenten gedeeld met derden?", "Enkel met onze AI-provider (Anthropic, gebonden aan een bewerkerovereenkomst) en MultiSafepay voor de betaling."],
      ["Is de verbinding beveiligd?", "Ja. Volledige HTTPS, AES-256 encryptie bij opslag van bestanden."],
      ["Voldoet OfferteExpert aan de GDPR?", "Ja. We hanteren een strikt privacybeleid en bewaartermijnen, en respecteren al je rechten als betrokkene."],
      ["Kan ik mijn gegevens laten verwijderen?", "Ja. Stuur een mail naar info@offerteexpert.be en we verwijderen je gegevens binnen 30 dagen."],
    ],
  },
  {
    cat: "Betaling",
    items: [
      ["Welke betaalmethodes worden ondersteund?", "Bancontact, iDEAL, Visa, Mastercard, Apple Pay en Google Pay — via MultiSafepay."],
      ["Is er een abonnement?", "Nee. Je betaalt enkel per analyse. Geen verborgen kosten."],
      ["Wat als ik niet tevreden ben met het rapport?", "Bij een technische storing krijg je je betaling integraal terug. Voor inhoudelijke ontevredenheid kan je contact opnemen via info@offerteexpert.be."],
      ["Ontvang ik een factuur?", "Ja. Een PDF-factuur wordt automatisch per e-mail verstuurd na betaling."],
    ],
  },
  {
    cat: "Na de Analyse",
    items: [
      ["Hoe lang is mijn rapport beschikbaar?", "30 dagen via je unieke URL. Daarna wordt het automatisch verwijderd."],
      ["Kan ik het rapport opnieuw downloaden?", "Ja, zolang je rapport binnen de 30-dagen periode valt."],
      ["Wat doe ik met de onderhandelingstips?", "Gebruik ze als gespreksbasis met je aannemer. Vraag specificaties op de posten die te hoog liggen."],
    ],
  },
];

export default function Faq() {
  const allItems = groups.flatMap((g) => g.items);
  return (
    <PublicLayout>
      <Seo
        title="Veelgestelde vragen — OfferteExpert"
        description="Alle antwoorden over hoe OfferteExpert werkt, privacy, betalingen, AI-analyse en rapporten."
        jsonLd={buildFaqSchema(allItems)}
      />
      <section className="oc-section">
        <div className="oc-container max-w-3xl">
          <span className="text-[13px] uppercase tracking-[0.1em] font-semibold text-oc-secondary">FAQ</span>
          <h1 className="font-display font-bold text-[40px] sm:text-[52px] leading-tight tracking-tight text-oc-primary mt-3">
            Veelgestelde vragen
          </h1>
          <p className="mt-4 text-oc-textSecondary text-[17px] leading-relaxed max-w-2xl">
            Antwoorden op de vragen die we het meest krijgen. Je vraag er niet bij? <a className="text-oc-secondary underline" href="/contact">Neem contact op</a>.
          </p>

          <div className="mt-12 space-y-12">
            {groups.map((g, gi) => (
              <div key={gi}>
                <h2 className="font-display font-bold text-oc-primary text-[24px] mb-4">{g.cat}</h2>
                <Accordion type="single" collapsible className="w-full">
                  {g.items.map(([q, a], i) => (
                    <AccordionItem
                      key={i}
                      value={`${gi}-${i}`}
                      className="border border-oc-border rounded-oc mb-3 px-6 data-[state=open]:bg-oc-subtle"
                    >
                      <AccordionTrigger className="font-display font-semibold text-oc-primary text-[17px] py-5 hover:no-underline text-left">
                        {q}
                      </AccordionTrigger>
                      <AccordionContent className="text-oc-textSecondary text-[15px] leading-relaxed pb-5">
                        {a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            ))}
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
